import type {GuessCorrectness} from 'resources/augmented/Guess/GuessModel.v1';

/**
 * The manual-grading states `read_for_grading` returns for a submitted guess.
 *
 * Only a teacher-gated read carries them, so an absent status means "no grading to report here"
 * (independent practice, a student's own view) rather than a state this module failed to model.
 */
export type FrqGradingStatus = 'ungraded' | 'grading_in_progress' | 'published';

export interface FrqGrading {
  status: FrqGradingStatus;
  /** 0..1. Meaningful once `status` is `published`; 0 before a grade exists. */
  grade: number;
}

/** `status` arrives from GraphQL as a bare string, so narrow it where the query is read. */
export function toFrqGradingStatus(status: string | undefined): FrqGradingStatus | undefined {
  return status === 'ungraded' || status === 'grading_in_progress' || status === 'published'
    ? status
    : undefined;
}

/**
 * A graded free response is always read as if partial credit were enabled: any credit at all is
 * partially correct, and only a zero is incorrect. A rubric scores in parts, so the assignment's
 * partial-credit setting (which decides whether a part-scored answer counts) has nothing to say
 * about a response a teacher scored 1 of 3.
 */
export function frqCorrectness(grade: number): GuessCorrectness {
  if (grade >= 1) {
    return 'correct';
  }
  return grade > 0 ? 'partiallyCorrect' : 'incorrect';
}
